// =============================================================================
// 루미나 화면 층 — 블랙 components/widgets/WidgetSlot.jsx 의 로직 그대로(슬롯 조회·필터·정렬·렌더러 6종), 위젯 인라인 스타일의 폴백값만
//   루미나 부품 규격(알약 버튼 44·안내 상자 16px 라운드·이미지 24px 라운드)으로 교체. WHY: SPEC §2. 슬롯 배치(HomePage 의 위치)는 블랙과 동일.
// WidgetSlot — 워드프레스 위젯 영역 패턴 (2026-05-20 Phase 3)
//
// 운영자가 사이트설정 > 디자인설정 > 위젯 배치에서 슬롯에 추가한 위젯을 렌더한다.
// designTemplate 은 앱이 받아 둔 값(DesignTemplateContext)을 쓴다. HomePage 등에서는 prop 없이
// <WidgetSlot slotKey="hero-banner" /> 만 배치하면 된다.
// =============================================================================
import React, { useContext } from 'react';
import DesignTemplateContext from '../../contexts/DesignTemplateContext';

// 위젯 타입별 렌더러
const WidgetRenderers = {
  text: (data) => (
    <div style={{ fontSize: data?.fontSize || 15, color: data?.color || 'inherit', lineHeight: 1.6 }}>
      {data?.text || ''}
    </div>
  ),
  image: (data) => {
    if (!data?.src) return null;
    /* [루미나] 이미지 위젯은 24px 라운드로 다듬는다(크기 규칙은 블랙 그대로) */
    const img = <img src={data.src} alt={data.alt || ''} style={{ maxWidth: data.width || '100%', maxHeight: data.height || 'auto', borderRadius: 24 }} loading="lazy" />;
    return data.link ? <a href={data.link} target={data.linkTarget || '_self'} rel="noopener noreferrer">{img}</a> : img;
  },
  button: (data) => {
    if (!data?.label) return null;
    /* [루미나] 작은 알약 버튼 규격(44px·999 라운드·차콜/흰) — 관리자 색 토큰이 있으면 그 값 우선(블랙과 같은 변수) */
    return (
      <a href={data.link || '#'} className={`widget-button widget-button-${data.variant || 'primary'}`}
         style={{ display: 'inline-flex', alignItems: 'center', height: 44, padding: '0 22px', background: 'var(--color-primary, var(--charcoal, #1D272C))', color: 'var(--color-background, #FFFFFF)', borderRadius: 999, fontSize: 15, fontWeight: 500, textDecoration: 'none' }}>
        {data.label}
      </a>
    );
  },
  slide: (data) => {
    const first = data?.slides?.[0];
    if (!first) return null;
    return WidgetRenderers.image({ src: first.image, alt: first.alt, link: first.link });
  },
  /* [루미나] 안내 상자 규격(16px 라운드·연한 바탕) — 관리자 색 토큰이 있으면 그 값 우선(블랙과 같은 변수) */
  notice: (data) => (
    <div style={{ padding: '12px 18px', background: 'var(--color-background-elevated, var(--mint-bg, #EAFBF1))', color: 'var(--color-text, var(--charcoal, #1D272C))', borderLeft: '3px solid var(--color-primary, var(--green, #00FF01))', borderRadius: 16, fontSize: 14, lineHeight: 1.6 }}>
      {data?.text || ''}
    </div>
  ),
  sns: (data) => {
    if (!data?.icon || !data?.link) return null;
    return (
      <a href={data.link} target="_blank" rel="noopener noreferrer">
        <img src={data.icon} alt={data.platform || 'sns'} style={{ width: data.size || 32, height: data.size || 32 }} />
      </a>
    );
  }
};

// designTemplate 우선순위:
//   1. props.designTemplate (호출자가 전달)
//   2. 앱이 받아 둔 디자인 설정(App.js 의 useDomainTheme → DesignTemplateProvider 로 모든 화면에 제공)
// [2026-09-30 운영자 "사이트 접속시 왜 느린거야?" → 진행] 종전에는 위젯 칸마다 useDomainTheme 을 따로 불러,
//   칸 수만큼 디자인 설정을 다시 받았다(홈 첫 접속에 같은 요청 10번, 요청마다 Cloudflare 경유 0.7~1초 — 실측).
//   같은 값을 앱이 이미 받아 나눠 주므로 그것을 읽는다(요청 0번, 토큰·색 적용은 앱 쪽 한 번으로 충분).
const WidgetSlot = ({ slotKey, designTemplate: propTemplate, className = '', style = {}, excludeTypes = [] }) => {
  const contextTemplate = useContext(DesignTemplateContext);
  const designTemplate = propTemplate || contextTemplate;

  const allWidgets = designTemplate?.widgets;
  if (!Array.isArray(allWidgets) || allWidgets.length === 0) return null;

  // [2026-06-09] excludeTypes: 이 슬롯에서 제외할 위젯 타입(기본 []=제외 없음, 기존 동작 무영향).
  //   히어로 슬라이더가 hero-banner 의 slide 위젯을 직접 소비하므로, 같은 슬롯의 WidgetSlot 에서는
  //   slide 를 제외해 중복 렌더를 막는다(image/button 등 다른 위젯은 그대로 렌더).
  const slotWidgets = allWidgets
    .filter(w => w.slot_key === slotKey && w.is_active === 1 && !excludeTypes.includes(w.widget_type))
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  if (slotWidgets.length === 0) return null;

  return (
    <div className={`widget-slot widget-slot-${slotKey} ${className}`.trim()} style={style}>
      {slotWidgets.map(widget => {
        const renderer = WidgetRenderers[widget.widget_type];
        if (!renderer) {
          console.warn(`[WidgetSlot] 알 수 없는 widget_type: ${widget.widget_type}`);
          return null;
        }
        let data = widget.widget_data;
        if (typeof data === 'string') {
          try { data = JSON.parse(data); } catch (e) { return null; }
        }
        return <div key={widget.id} className={`widget widget-${widget.widget_type}`}>{renderer(data)}</div>;
      })}
    </div>
  );
};

export default WidgetSlot;
